import React, { useState, useEffect, useCallback } from "react";
import { Select, Spin, Alert } from "antd";
import { SyncOutlined, DownloadOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import api from "../../../api";
import SLASummaryCards from "../cards/SLASummaryCards";
import SLAUserComplianceChart from "../charts/SLAUserComplianceChart";
import SLATeamComparisonChart from "../charts/SLATeamComparisonChart";
import SLAIndividualPerformanceCards from "../cards/SLAIndividualPerformanceCards";
import SLARequirementCompliance from "../SLARequirementCompliance";
import { FolderSLATabProps } from "../types/folderSla.types";

const { Option } = Select;

const RequirementVerificationTab: React.FC<FolderSLATabProps> = ({
  period,
  dateRange,
  filterType,
}) => {
  const [selectedTeam, setSelectedTeam] = useState<string>("all");
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [teamData, setTeamData] = useState<any>(null);
  const [userData, setUserData] = useState<any>(null);
  const [opsAdminUsers, setOpsAdminUsers] = useState<any>(null);

  const buildQueryParams = useCallback(() => {
    const params: Record<string, string> = {};

    if (filterType === "preset") {
      const periodMapping: Record<string, string> = {
        week: "last_7_days",
        month: "last_30_days",
        quarter: "last_90_days",
      };
      params.period = periodMapping[period] || period;
    } else if (filterType === "custom" && dateRange) {
      params.start_date = dateRange[0];
      params.end_date = dateRange[1];
    }

    return new URLSearchParams(params).toString();
  }, [filterType, period, dateRange]);

  const fetchData = useCallback(async () => {
    if (filterType === "custom" && !dateRange) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const queryString = buildQueryParams();

      // Fetch team data
      const teamResponse = await api.get(
        `/metrics/sla/charts/teams/?${queryString}`
      );
      setTeamData(teamResponse.data);

      // Fetch user data based on selected team
      let userResponse;
      if (selectedTeam !== "all") {
        const userParams = new URLSearchParams(queryString);
        userParams.set("team", selectedTeam);
        userResponse = await api.get(
          `/metrics/sla/charts/users/?${userParams.toString()}`
        );
      } else {
        try {
          const [operationsResponse, cjaResponse, opsAdminResponse] =
            await Promise.all([
              api.get(
                `/metrics/sla/charts/users/?${queryString}&team=operations`
              ),
              api.get(`/metrics/sla/charts/users/?${queryString}&team=cja`),
              api.get(
                `/metrics/sla/charts/users/?${queryString}&team=ops-admin&level=direct`
              ),
            ]);

          const combinedUserData = {
            before_cutoff_compliance: [
              ...(operationsResponse.data.before_cutoff_compliance || []),
              ...(cjaResponse.data.before_cutoff_compliance || []),
            ],
            after_cutoff_compliance: [
              ...(operationsResponse.data.after_cutoff_compliance || []),
              ...(cjaResponse.data.after_cutoff_compliance || []),
            ],
            combined_compliance: [
              ...(operationsResponse.data.combined_compliance || []),
              ...(cjaResponse.data.combined_compliance || []),
            ],
          };

          userResponse = { data: combinedUserData };
          setOpsAdminUsers(opsAdminResponse.data.combined_compliance || []);
        } catch (teamError) {
          console.warn("Error fetching some team data:", teamError);
          userResponse = {
            data: {
              before_cutoff_compliance: [],
              after_cutoff_compliance: [],
              combined_compliance: [],
            },
          };
        }
      }

      setUserData(userResponse.data);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching SLA data:", err);
      setError(
        `Error al cargar métricas SLA: ${err.response?.data?.message || err?.message}`
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedTeam, buildQueryParams, filterType, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTeamChange = (value: string) => {
    setSelectedTeam(value);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const exportToCsv = () => {
    if (!userData || !userData.combined_compliance) return;

    const convertToCSV = (objArray: any[], headerList: string[]) => {
      const array =
        typeof objArray !== "object" ? JSON.parse(objArray) : objArray;
      let str = headerList.join(",") + "\r\n";

      for (let i = 0; i < array.length; i++) {
        let line = "";
        for (const index in headerList) {
          const head = headerList[index];
          if (line !== "") line += ",";
          line += array[i][head] !== undefined ? array[i][head] : "";
        }
        str += line + "\r\n";
      }
      return str;
    };

    const slaHeaders = [
      "user_name",
      "user_email",
      "compliance_pct",
      "before_cutoff_pct",
      "after_cutoff_pct",
      "total_processed",
      "compliant",
    ];
    const slaRows = userData.combined_compliance
      .filter((user: any) => user.total_processed > 0)
      .map((user: any) => ({
        user_name: user.user_name,
        user_email: user.user_email,
        compliance_pct: user.compliance_pct,
        before_cutoff_pct: user.before_cutoff_pct,
        after_cutoff_pct: user.after_cutoff_pct,
        total_processed: user.total_processed,
        compliant: user.compliant,
      }));

    const csvContent = convertToCSV(slaRows, slaHeaders);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `sla-metrics-data-${format(new Date(), "yyyy-MM-dd")}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Equipo:</span>
          <Select
            className="w-40"
            onChange={handleTeamChange}
            value={selectedTeam}
            placeholder="Seleccionar Equipo"
          >
            <Option value="all">Todos los Equipos</Option>
            <Option value="operations">Operations</Option>
            <Option value="cja">CJA</Option>
          </Select>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <SyncOutlined spin={refreshing} className="mr-1" />
            Actualizar
          </button>
          <button
            onClick={exportToCsv}
            disabled={loading || !userData}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <DownloadOutlined className="mr-1" />
            Exportar
          </button>
        </div>
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      {loading ? (
        <div className="flex justify-center items-center py-24">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* SLA Summary Cards */}
          <SLASummaryCards teamData={teamData} userData={userData} />

          {/* SLA User Compliance Chart */}
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-medium mb-4 text-gray-700">
              Cumplimiento SLA por Usuario
              {selectedTeam !== "all" && (
                <span className="text-sm text-gray-500 ml-2 capitalize">
                  - Equipo {selectedTeam}
                </span>
              )}
            </h2>
            <SLAUserComplianceChart userData={userData} />
          </div>

          {/* SLA Team Comparison */}
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-medium mb-4 text-gray-700">
              Comparación por Equipo
            </h2>
            <SLATeamComparisonChart teamData={teamData} />
          </div>

          {/* Individual Performance Cards */}
          <SLAIndividualPerformanceCards
            userData={userData}
            selectedTeam={selectedTeam}
            opsAdminUsers={opsAdminUsers}
          />

          {/* Requirement Compliance - Collapsible Section */}
          <SLARequirementCompliance
            dateRange={dateRange}
            selectedUser={selectedTeam !== "all" ? undefined : undefined}
            period={period}
            filterType={filterType}
          />
        </>
      )}
    </div>
  );
};

export default RequirementVerificationTab;
